import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Zap, Layers, Ban, Package, Mail } from "lucide-react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";
const EMAIL = "hello@kalzastudio.com";

const LOGOS = [
  { src: "/images/brand-articulations.png", alt: "Brand Articulations" },
  { src: "/images/uefa.png", alt: "UEFA Champions League" },
  { src: "/images/pyszne.png", alt: "Pyszne.pl" },
  { src: "/images/coca-cola.png", alt: "Coca-Cola" },
  { src: "/images/lukkien.png", alt: "Lukkien" },
  { src: "/images/just-eat.png", alt: "Just Eat Takeaway" },
  { src: "/images/house-of-retouching.png", alt: "House of Retouching" },
  { src: "/images/samsung.png", alt: "Samsung" },
  { src: "/images/ben-jerrys.png", alt: "Ben & Jerry\'s" },
  { src: "/images/heineken.png", alt: "Heineken" },
];

const OrganicImage = ({ src, alt, className = "" }) => (
  <div className={`aspect-[4/5] overflow-hidden max-w-[65%] lg:max-w-none ${className}`} style={{ WebkitMaskImage: 'url(/images/mood/shape-mask.png)', maskImage: 'url(/images/mood/shape-mask.png)', WebkitMaskSize: '100% 100%', maskSize: '100% 100%', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat' }}>
    <img src={src} alt={alt || ""} className="w-full h-full object-cover" />
  </div>
);

export default function HomePagePL() {
  return (
    <main data-testid="home-page-pl">
      {/* ═══ 1. HERO (dark) ═══ */}
      <section className="relative min-h-screen pt-24">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 min-h-[calc(100vh-6rem)] items-center py-12">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="relative order-2 lg:order-1">
              <OrganicImage src="/images/ola-hero.jpg" alt="Aleksandra Kałuża, KALZA Studio" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="order-1 lg:order-2">
              <span className="font-eyebrow text-white mb-6 block">STRATEGIA MARKI I KIERUNEK KREATYWNY</span>
              <h1 className="mb-6">
                <span className="font-display text-4xl md:text-5xl lg:text-6xl text-white block leading-[0.95]">JASNOŚĆ, KTÓRA</span>
                <span className="font-display text-4xl md:text-5xl lg:text-6xl text-white block leading-[0.95]">ODBLOKUJE TWÓJ BIZNES</span>
              </h1>
              <p className="font-body text-base md:text-lg text-white/60 leading-relaxed mb-4 max-w-md">
                Pomagam osobom prowadzącym własne biznesy zobaczyć to, czego same nie widzą w swojej marce. Nazywam co nie gra i buduję strategię, po której wraca energia do działania. Koniec ze zgadywaniem. Czas na decyzje.
              </p>
              <p className="font-body text-xs text-white/40 mb-8 max-w-md">
                15 lat pracy z markami i globalnymi kampaniami kreatywnymi.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#BE123C] transition-colors duration-300">
                  UMÓW ROZMOWĘ <ArrowUpRight size={16} />
                </a>
                <Link to="/about" className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/60 font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:text-white hover:border-white/40 transition-colors duration-300">
                  O Aleksandrze
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 2. LOGO CAROUSEL (white) ═══ */}
      <section className="py-10 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <p className="font-eyebrow text-center text-[#6B6560] mb-8">MARKI, Z KTÓRYMI PRACOWAŁAM</p>
        </div>
        <div className="relative overflow-hidden">
          <div className="flex animate-logo-scroll-pl gap-16 items-center px-8">
            {[...LOGOS, ...LOGOS, ...LOGOS].map((logo, i) => (
              <img key={i} src={logo.src} alt={logo.alt} className="flex-shrink-0 h-6 md:h-8 w-auto object-contain invert grayscale opacity-50 hover:opacity-80 transition-all duration-300 mix-blend-multiply" />
            ))}
          </div>
        </div>
        <style>{`
          @keyframes logoScrollPl {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.33%); }
          }
          .animate-logo-scroll-pl {
            animation: logoScrollPl 40s linear infinite;
            width: max-content;
          }
          .animate-logo-scroll-pl:hover {
            animation-play-state: paused;
          }
        `}</style>
      </section>

      {/* ═══ 3. PRAWDZIWY PROBLEM (Apple grey) ═══ */}
      <section className="py-24 md:py-32 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">PRAWDZIWY PROBLEM</span>
              <h2 className="mb-10">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">TWÓJ BIZNES PRZERÓSŁ</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-2">swoją markę</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7] max-w-lg">
                <p>Ludzie, którzy z Tobą pracują, wiedzą ile jesteś warta. Problem zaczyna się wcześniej. Ktoś trafia na Twoją stronę, przewija, czyta opis oferty i wychodzi, bo nic na tej stronie nie mówi: zostań, to jest to czego szukasz.</p>
                <p className="text-[#4A4540]">Twój biznes jest lepszy niż to, jak wygląda w internecie. I ta luka kosztuje Cię czas, pieniądze i klientów, na których Ci naprawdę zależy.</p>
                <p className="font-accent text-[#BE123C] text-lg mt-6">Rozpoznajesz się w tym? Pewnie w jednym z opisów poniżej.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-col justify-center space-y-10">
              {[
                { title: "Ludzie wchodzą na Twoją stronę i wychodzą", body: "Marka wygląda w porządku, no ale nic na niej nie zatrzymuje. Brakuje powodu, żeby zostać." },
                { title: "Podnoszenie cen to walka", body: "Kiedy wartość nie jest widoczna od pierwszego kontaktu, każda rozmowa o pieniądzach staje się trudniejsza niż powinna." },
                { title: "Klienci, których pragniesz, przechodzą obok", body: "Przechodzą obok, bo z Twojej strony nie widać, że do siebie pasujecie." },
              ].map((card, i) => (
                <div key={i} className="border-l-2 border-[#BE123C] pl-6">
                  <h3 className="font-body text-base font-semibold text-[#1C1210] mb-1.5">{card.title}</h3>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{card.body}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 4. DLA KOGO (white) ═══ */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">DLA KOGO TO JEST</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">DLA OSÓB, KTÓRE CZUJĄ</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">że ich marka nie nadąża</span>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95] mt-1">ZA TYM, KIM SIĘ STAŁY</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Na starcie", body: "Wiesz co robisz i robisz to dobrze. No ale kiedy trzeba o tym opowiedzieć albo pokazać to w sieci, słowa brzmią jak u wszystkich w branży. Marka jeszcze nie brzmi jak Ty.", img: "/images/mood/arch-sea.jpg" },
              { title: "W transformacji", body: "Zmieniłaś się, masz inny kierunek niż dwa lata temu. A marka wciąż opowiada starą historię. Budujesz teraz z innego miejsca i Twoja komunikacja powinna to pokazywać.", img: "/images/mood/water-light.jpg" },
              { title: "Z doświadczeniem", body: "Biznes ma trakcję. Klienci wracają, polecają Cię, ufają Twojej pracy. Jesteś gotowa podnieść ceny. No ale Twoja strona i Instagram nie pokazują wartości, którą faktycznie dajesz.", img: "/images/mood/wood-amber.jpg" },
            ].map((card, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }} className="overflow-hidden">
                <div className="h-64 md:h-72 overflow-hidden">
                  <img src={card.img} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="pt-6 pb-2">
                  <h3 className="font-serif text-lg text-[#1C1210] mb-3">{card.title}</h3>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{card.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 5. KTO ZA TYM STOI (image bg) ═══ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/kalza-bg.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <OrganicImage src="/images/ola-about.jpg" alt="Aleksandra Kałuża" className="shadow-2xl" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">KTO ZA TYM STOI</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">CZEŚĆ, JESTEM</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">Aleksandra</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#4A4540] leading-[1.7]">
                <p>Przez lata prowadziłam globalną pracę kreatywną w międzynarodowych firmach. Współpracowałam z markami takimi jak Just Eat Takeaway, Heineken, Coca-Cola, Ben & Jerry's, Samsung i UEFA Champions League.</p>
                <p>Nigdy nie chodziło mi o to, żeby rzeczy ładnie wyglądały. Obserwowałam jak ludzie reagują na marki, w jakich momentach zaufanie się buduje i kiedy się traci. Teraz robię to samo, tylko dla osób budujących własne biznesy.</p>
              </div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  "15+ lat w strategii marki i pracy kreatywnej",
                  "100+ marek globalnych i osobistych",
                  "Zarządzanie zespołami 40+ osób w Europie",
                  "Kampanie za miliony euro dla Heineken, Samsung i innych",
                ].map((proof, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#BE123C] flex-shrink-0 mt-2" />
                    <span className="font-body text-xs text-[#6B6560]">{proof}</span>
                  </div>
                ))}
              </div>
              <Link to="/about" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 mt-8 hover:bg-[#9F1239] transition-colors duration-300">
                WIĘCEJ O MNIE <ArrowUpRight size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 6. MOJE PODEJŚCIE (DARK) ═══ */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/75" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-white mb-6 block">MOJE PODEJŚCIE</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-white block leading-[0.95]">WIDZĘ RZECZY, KTÓRE</span>
                <span className="font-accent text-3xl md:text-4xl text-[#E11D48] block leading-[1.1] mt-1">Ty pomijasz, bo jesteś za blisko</span>
              </h2>
              <p className="font-body text-base text-white/70 leading-[1.7]">
                Mój sposób pracy wynika z lat czytania marek od środka, prowadzenia zespołów kreatywnych i obserwowania co sprawia, że ludzie zaczynają komuś ufać. Sięgam po psychologię zachowań, archetypy, psychologię narracji i semiotykę, żeby dotrzeć do tego co faktycznie nie gra. Nie pracuję z gotowca. Każdy biznes dostaje własny proces, dopasowany do tego co jest w nim prawdziwe.
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-col justify-center space-y-8">
              {[
                { Icon: Zap, title: "Szybko widzę, gdzie jest rozjazd.", body: "Większość właścicieli biznesu wie, że coś jest nie tak. Zazwyczaj potrafię to nazwać w pierwszej rozmowie." },
                { Icon: Layers, title: "Wchodzę głębiej.", body: "Archetypy, psychologia kupującego, struktura narracji, semiotyka. Używam tego co w danym przypadku ma sens." },
                { Icon: Ban, title: "Żadnych gotowców.", body: "Każdy biznes dostaje własny proces, zbudowany wokół tego co jest w nim prawdziwe." },
                { Icon: Package, title: "Efekt jest praktyczny.", body: "Słowa, które przyciągają uwagę, wizualia, które niosą charakter Twojego biznesu i brand book, który trzyma to wszystko w całości." },
              ].map((point, i) => (
                <div key={i} className="flex items-start gap-4">
                  <point.Icon size={18} className="text-[#E11D48] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-body text-sm font-semibold text-white mb-0.5">{point.title}</h3>
                    <p className="font-body text-sm text-white/60 leading-relaxed">{point.body}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 7. WSPÓŁPRACA (white) ═══ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">CZTERY ŚCIEŻKI</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">WSPÓŁPRACA</span>
              </h2>
              <p className="font-body text-base text-[#6B6560] leading-[1.7] max-w-lg mb-6">
                Bezpośrednia praca między Tobą a mną. Wybierz ścieżkę, która pasuje do tego, gdzie teraz jest Twój biznes. Od szybkiej diagnozy po pełny system marki.
              </p>
              <div className="space-y-2 mb-8">
                <p className="font-body text-sm text-[#4A4540] leading-relaxed">
                  <span className="font-semibold text-[#1C1210]">Zacznij tutaj, jeśli</span> chcesz, żeby ktoś nazwał co nie gra, pokazał co z tym zrobić albo zbudował to razem z Tobą.
                </p>
                <p className="font-body text-sm text-[#9A9590] leading-relaxed italic">
                  To nie jest dla Ciebie, jeśli szukasz samego logo albo chcesz oddać wszystko bez strategicznych rozmów.
                </p>
              </div>
              <Link to="/pl/services" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#9F1239] transition-colors duration-300">
                ZOBACZ USŁUGI <ArrowRight size={14} />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="space-y-5">
                {[
                  { label: "DIAGNOZA", title: "Brand Reality Check Audit", duration: "7 dni" },
                  { label: "POZYCJONOWANIE", title: "Brand Foundation", duration: "2 do 4 tygodni" },
                  { label: "PEŁNA EKSPRESJA", title: "Full Brand System", duration: "~2 miesiące" },
                  { label: "SZYBKA ROZMOWA", title: "Konsultacja 1:1", duration: "90 min" },
                ].map((s, i) => (
                  <div key={i} className="flex items-start justify-between gap-6 border-b border-[#1C1210]/10 pb-5 last:border-b-0">
                    <div>
                      <span className="inline-block font-eyebrow text-[10px] tracking-[2px] text-[#8B1A3A] mb-1.5">{s.label}</span>
                      <h3 className="font-serif text-lg text-[#1C1210]">{s.title}</h3>
                    </div>
                    <span className="font-body text-xs text-[#6B6560]/70 uppercase tracking-[1px] whitespace-nowrap mt-1.5">{s.duration}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 8. PRODUKTY AI (Apple grey) ═══ */}
      <section className="py-20 md:py-28 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">NARZĘDZIA AI</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">PRODUKTY AI</span>
              </h2>
              <p className="font-body text-base text-[#6B6560] leading-[1.7] max-w-lg mb-6">
                Precyzyjne narzędzia AI oparte na tym samym myśleniu o marce, które stosuję w pracy z klientami. Jednorazowy zakup, działają w Claude lub ChatGPT, Twoje na zawsze.
              </p>
              <div className="space-y-2 mb-8">
                <p className="font-body text-sm text-[#4A4540] leading-relaxed">
                  <span className="font-semibold text-[#1C1210]">Zacznij tutaj, jeśli</span> wolisz pracować samodzielnie, ale z narzędziem w które jest wbudowane prawdziwe myślenie o marce.
                </p>
                <p className="font-body text-sm text-[#9A9590] leading-relaxed italic">
                  To nie jest dla Ciebie, jeśli potrzebujesz kogoś kto zrobi pracę strategiczną za Ciebie. Do tego zobacz usługi powyżej.
                </p>
              </div>
              <Link to="/pl/ai-products" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#9F1239] transition-colors duration-300">
                PRODUKTY AI <ArrowRight size={14} />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="space-y-5">
                {[
                  { label: "AUDYT TREŚCI", title: "Make Me Memorable", price: "€29" },
                  { label: "INSTAGRAM STORIES", title: "Stories That Sell", price: "€49" },
                  { label: "DIAGNOSTYKA TOŻSAMOŚCI", title: "Brand Blind Spots", price: "€69" },
                  { label: "OSOBISTY AGENT AI", title: "DNA Voice", price: "€97" },
                ].map((p, i) => (
                  <div key={i} className="flex items-start justify-between gap-6 border-b border-[#1C1210]/10 pb-5 last:border-b-0">
                    <div>
                      <span className="inline-block font-eyebrow text-[10px] tracking-[2px] text-[#8B1A3A] mb-1.5">{p.label}</span>
                      <h3 className="font-serif text-lg text-[#1C1210]">{p.title}</h3>
                    </div>
                    <span className="font-body text-sm text-[#1C1210] font-medium whitespace-nowrap mt-1.5">{p.price}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 9. TESTIMONIAL (kalza-bg) ═══ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/kalza-bg.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-12 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">OPINIE</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">CO MÓWIĄ LUDZIE</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">po współpracy ze mną</span>
            </h2>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="max-w-5xl">
            <div className="bg-white p-8 md:p-12 relative border border-[#e5e2de] shadow-lg">
              <span className="absolute left-6 top-4 text-7xl font-serif text-[#BE123C]/10 select-none pointer-events-none leading-none">&ldquo;</span>
              <div className="relative space-y-4 font-body text-base md:text-lg text-[#4A4540] leading-relaxed max-w-3xl">
                <p>Wcześniej nie widziałem zbyt dużej wartości w tym, co robię. Przychodziłem do pracy dla wypłaty, nie dla ludzi.</p>
                <p>Te warsztaty pokazały mi, co faktycznie oferuję, jakie są moje prawdziwe mocne strony i co naprawdę chcę reprezentować. Moim atutem nigdy nie była wiedza techniczna. To była moja osobowość i sposób, w jaki podchodzę do ludzi.</p>
                <p>Praca znów ma kolor. Teraz budzę się z chęcią do pracy, a nie czułem tego od dłuższego czasu.</p>
              </div>
              <p className="font-body text-sm text-[#9A9590] mt-8 tracking-wider uppercase">Grzegorz Galikowski, Galikowski Finance, Founder</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 10. NEWSLETTER (Apple grey) ═══ */}
      <section className="py-20 md:py-28 bg-[#f5f5f7]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="aspect-[4/5] overflow-hidden bg-[#ebebed] max-w-[65%] lg:max-w-none">
                <img src="/images/ola-newsletter.jpg" alt="Aleksandra Kałuża" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">DLA TYCH, KTÓRZY WOLĄ WIEDZIEĆ</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">DROGI WŁAŚCICIELU</span>
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">BIZNESU,</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">przyjrzyj się bliżej.</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7] mb-8">
                <p>Co mówią o Tobie ludzie, kiedy wychodzisz z pokoju? Czy w ogóle wiesz?</p>
                <p>Szczere obserwacje o markach i ludziach, którzy za nimi stoją. Co działa, co nie i dlaczego. Bez szablonów, bez cotygodniowego szumu.</p>
              </div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const emailInput = e.target.email;
                  const btn = e.target.querySelector('button');
                  const msg = e.target.querySelector('.form-msg');
                  btn.disabled = true;
                  btn.textContent = 'Wysyłam...';
                  try {
                    const res = await fetch('https://connect.mailerlite.com/api/subscribers', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': 'Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJhdWQiOiI0IiwianRpIjoiODIxOTY0NWMzMTE5NjE2YzczNjY3NDU5MTU5MGI2OWQyYTVlZDY2NjhmMWRmYWRjNWMxMTI3MDEyY2FmMjI4YWVmYTc0MzNmZmMzMjliYzkiLCJpYXQiOjE3Nzc0NTkyNTAuMjc1ODI2LCJuYmYiOjE3Nzc0NTkyNTAuMjc1ODI5LCJleHAiOjQ5MzMxMzI4NTAuMjcwMjg2LCJzdWIiOiIyMzAyODUyIiwic2NvcGVzIjpbXX0.HgZ-TwYNlT9vK2ssOiY_TVCxj5pZIsuwE_z7glvgbJpevnxbYLOYq7CTn9LVAsZowecF7IX-U7TWBNdT4pCOpQUZU2IfOFoydbmEJ420BaCKd_8bxxVVa-EjXc5hFAQxmds-bbzm3hGRjZXuBfkZPXe8L5PQNLO4O8oXSQZ1l3A0HcYHux4fsWSpQSlK_3KXYXR4UR6uBojDqFBZR1oqmmIDxSV41zJ-BONhS0XfP3yJ26cBrWYWGKvusOPFDyaV3OHHOGhIqOye0OLjEYITEefG4MM_RMCV_9nzi9CnUIcC3FlkPrd_YfMRgpgrb_TOEYROVXQNwsuqiyVv-VGwypCEKB6LuckybuUQCMojwFrFZqf-_uSM97GOZOSSaeE3TiR9vKP3lbGeWtGqrS3CPBi9DmVioWIHTh2WYKw5xVNETWIr-G5Xfsbna8LG6gclbLpKac6WMVkMgZhM_fL2bo_EaiXaMEJVMgb9szAgsyF_cQ3LuD2QZIC-Rt6tiu3FyEj2FIPzmq-hyiEDqC4IzTXT3xSZ92G5iERocoMBfkF4R9yRCjFmHX37drkuYksQ5kc_9-VV4c_8Ve_n3BYvbHnn0E3q6xF3lPHe8sOf75Qvd1Y4L2ipd3V4dcoOvtFxjym5JMu_m4EupXtELbV-xbw5Ne-fe6_Y5vQh_Rw0PRs',
                      },
                      body: JSON.stringify({
                        email: emailInput.value,
                        groups: ['186079588714022637'],
                      }),
                    });
                    if (res.ok) {
                      msg.textContent = 'Jesteś na liście. Sprawdź skrzynkę.';
                      msg.className = 'form-msg font-body text-sm text-[#BE123C] mt-4';
                      emailInput.value = '';
                    } else {
                      msg.textContent = 'Coś poszło nie tak. Spróbuj ponownie.';
                      msg.className = 'form-msg font-body text-sm text-[#9A9590] mt-4';
                    }
                  } catch {
                    msg.textContent = 'Coś poszło nie tak. Spróbuj ponownie.';
                    msg.className = 'form-msg font-body text-sm text-[#9A9590] mt-4';
                  }
                  btn.disabled = false;
                  btn.textContent = 'Zapisz się';
                }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <input type="email" name="email" required placeholder="twoj@email.com" className="flex-1 border border-[#1C1210]/15 bg-white px-5 py-4 font-body text-sm text-[#1C1210] placeholder:text-[#6B6560]/60 focus:outline-none focus:border-[#BE123C] transition-colors duration-300" />
                <button type="submit" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#9F1239] transition-colors duration-300">
                  Zapisz się <ArrowRight size={14} />
                </button>
              </form>
              <p className="form-msg font-body text-xs text-[#6B6560]/60 mt-4">
                Zero spamu. Wypisz się kiedy chcesz.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 11. FINAL CTA (dark) ═══ */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/70" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-white mb-6 block">WIDZISZ, JAK TO WYGLĄDA</span>
            <h2 className="mb-8">
              <span className="font-display text-3xl md:text-5xl text-white block leading-[0.95]">TWOJA KOLEJ</span>
            </h2>
            <p className="font-body text-base md:text-lg text-white/70 leading-relaxed mb-10 max-w-xl">
              Jeśli Twój biznes jest lepszy niż to, jak wygląda w sieci, powinnyśmy porozmawiać. Umów rozmowę, a powiem Ci dokładnie co widzę.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
                UMÓW ROZMOWĘ <ArrowUpRight size={16} />
              </a>
              <a href={`mailto:${EMAIL}`} className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/70 font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:text-white hover:border-white/40 transition-colors duration-300">
                <Mail size={14} /> {EMAIL}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
